import * as THREE from 'three'
import { FrameGizmo, SectionFrame } from '@/shared/sectionFrames'

/**
 * Wire the section gizmo to the V2 viewer's canvas.
 *
 * Kept out of ThreeViewer deliberately. That class is shared by every product-profile
 * screen including the cylindrical ones, and a gizmo that only applies to frame sections
 * has no business adding pointer handlers there. This attaches on demand and detaches
 * cleanly, so a cylindrical profile never sees it.
 */

export interface FrameGizmoHandle {
  /** Show the gizmo on a frame, or hide it with null. */
  show(frame: SectionFrame | null): void
  detach(): void
}

export interface AttachOptions {
  /** Fired continuously during a drag, so the preview tracks the pointer. */
  onChange(frame: SectionFrame): void
  /** Fired once on release — the point at which an edit is worth recording. */
  onCommit?(frame: SectionFrame): void
}

export default function attachFrameGizmo(options: AttachOptions): FrameGizmoHandle | null {
  const viewer = (window as any).threeViewer
  const canvas: HTMLCanvasElement | undefined = viewer?.renderer?.domElement
  const camera: THREE.Camera | undefined = viewer?.camera
  if (!viewer || !canvas || !camera) return null

  // Reuse the gizmo already attached to this viewer rather than stacking listeners.
  const existing: FrameGizmoHandle | undefined = viewer.__frameGizmoHandle
  if (existing) return existing

  const gizmo = new FrameGizmo()
  gizmo.visible = false

  const raycaster = new THREE.Raycaster()
  let current: SectionFrame | null = null
  let latest: SectionFrame | null = null
  let controlsWereEnabled = true

  const rayFrom = (event: PointerEvent): THREE.Ray => {
    const rect = canvas.getBoundingClientRect()
    const ndc = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    )
    raycaster.setFromCamera(ndc, camera as THREE.PerspectiveCamera)
    return raycaster.ray
  }

  const onPointerDown = (event: PointerEvent) => {
    if (event.button !== 0 || !current) return
    if (!gizmo.beginDrag(rayFrom(event))) return

    // Suspend orbiting for the duration, or dragging a handle spins the camera too.
    controlsWereEnabled = viewer.controls?.enabled ?? true
    if (viewer.controls) viewer.controls.enabled = false

    canvas.setPointerCapture(event.pointerId)
    event.preventDefault()
    event.stopPropagation()
  }

  const onPointerMove = (event: PointerEvent) => {
    if (!gizmo.dragging) {
      // Hover feedback costs one plane intersection and tells the operator which part
      // of the gizmo is live before they commit to a drag.
      if (current) {
        const over = gizmo.hitTest(rayFrom(event))
        canvas.style.cursor = over === 'rotate' ? 'grab' : over === 'resize' ? 'nwse-resize' : over ? 'move' : ''
      }
      return
    }

    const next = gizmo.updateDrag(rayFrom(event))
    if (!next) return
    latest = next
    current = next
    gizmo.update(next, viewer.product ?? null)
    options.onChange(next)
    event.preventDefault()
    event.stopPropagation()
  }

  const onPointerUp = (event: PointerEvent) => {
    if (!gizmo.dragging) return
    gizmo.endDrag()
    if (viewer.controls) viewer.controls.enabled = controlsWereEnabled
    if (canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId)
    if (latest) options.onCommit?.(latest)
    latest = null
  }

  // Capture phase: the viewer's own click handler drives target-object picking, and a
  // drag that ends on the model would otherwise register as a pick.
  canvas.addEventListener('pointerdown', onPointerDown, true)
  canvas.addEventListener('pointermove', onPointerMove, true)
  canvas.addEventListener('pointerup', onPointerUp, true)

  const handle: FrameGizmoHandle = {
    show(frame) {
      current = frame
      // Parent to the PRODUCT, not the scene.
      //
      // FrameGizmo places itself with frameMatrix(frame), and a frame is authored in the
      // product root's local space — so hanging it off the scene is only correct while the
      // root sits at the origin. normalizeProduct now shifts the root to centre the product
      // on the world Y axis, which models that were not modelled centred need, and that
      // left the gizmo behind by exactly the shift. As a child of the root the two cannot
      // drift apart again, whatever the root is doing.
      //
      // Re-checked on every show because loading a different product replaces the root and
      // takes its children with it.
      const root = viewer.product ?? null
      if (root && gizmo.parent !== root) root.add(gizmo)
      gizmo.update(frame, root)
    },
    detach() {
      canvas.removeEventListener('pointerdown', onPointerDown, true)
      canvas.removeEventListener('pointermove', onPointerMove, true)
      canvas.removeEventListener('pointerup', onPointerUp, true)
      canvas.style.cursor = ''
      gizmo.removeFromParent()
      gizmo.dispose()
      viewer.__frameGizmoHandle = undefined
    },
  }

  viewer.__frameGizmoHandle = handle
  return handle
}
